import Feature from "ol/Feature";
import RenderFeature from "ol/render/Feature";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Text from "ol/style/Text";
import Icon from "ol/style/Icon";
import { Point, Polygon } from "ol/geom";
import { fromLonLat } from "ol/proj";
import ImageStatic from "ol/source/ImageStatic";

import windIcon from "@/img/wind-direction-map-icon.svg";
import CircleStyle from "ol/style/Circle.js";
import {
	DataWeatherStation,
	IMergedObservationAndStation,
	IStationWithObservation,
	observationsType,
} from "@/schemas/observation-schema";
import { AreaPolygonType } from "@/blocks/warnings-map/types/area-polygon-type";
import { hexToRgba } from "@/helpers/hex-to-rgba";
import { formatWeatherStateIcon } from "@/helpers/format-weather-state-icon";
import { MAX_OPACITY_GEOTIFF } from "./map-constant";
import { IForecastWithStation } from "@/types/daily-forecast-type";
import { GeoController } from "./types/map-type";
import ImageLayer from "ol/layer/Image";

export const createStyleFn = (): ((
	feature: Feature | RenderFeature,
) => Style | undefined) => {
	return (_: Feature | RenderFeature): Style | undefined => {
		return new Style({
			fill: new Fill({
				color: "#E7E7E8",
			}),
		});
	};
};

export const imageryStroke = () => {
	return () => {
		new Style({
			stroke: new Stroke({
				color: "orange",
				width: 1,
			}),
		});
	};
};

export const displayForecastDataWithStation = (
	stationWithData: IForecastWithStation[],
	selectedDate: Date,
	isArabic: boolean,
) => {
	const isSameDay = (date1: Date, date2: Date) =>
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate();

	return stationWithData.map((item) => {
		const [lon, lat] = item.coordinates;

		const marker = new Feature({
			geometry: new Point(fromLonLat([Number(lon), Number(lat)])),
			data: item,
		});

		const styles: Style[] = [];

		const hasForecast =
			item.date !== undefined &&
			item.ww !== undefined &&
			isSameDay(item.date, selectedDate);

		if (hasForecast) {
			const src =
				item?.ww && item.date
					? formatWeatherStateIcon({
							status: item.ww,
							timeStep: item.date.toISOString(),
					  })
					: undefined;

			if (src) {
				styles.push(
					new Style({
						image: new Icon({
							src,
							opacity: 1,
							scale: 0.04,
						}),
					}),
				);
			}
		}

		styles.push(
			new Style({
				text: new Text({
					text: isArabic ? item.name_ar : item.name_en,
					font: "12px sans-serif",
					fill: new Fill({ color: "#000" }),
					stroke: new Stroke({ color: "#fff" }),
					offsetY: 28,
					textAlign: "center",
					textBaseline: "middle",
				}),
			}),
		);

		marker.setStyle(styles);
		marker.set("stationId", item.station_id);
		marker.set("priority", item.priority);
		marker.set("defaultStyle", styles);

		return marker;
	});
};

export const formatMapsIconToDisplay = ({
	data = [],
	selectedfilter,
	showStationLabelAndImage,
}: {
	data?: IStationWithObservation[];
	selectedfilter?: keyof DataWeatherStation;
	showStationLabelAndImage: boolean;
}) => {
	return data.map((item) => {
		const [lon, lat] = item.position;

		const marker = new Feature({
			geometry: new Point(fromLonLat([Number(lon), Number(lat)])),
			data: item,
		});

		if (selectedfilter) {
			const value = item.data[selectedfilter];

			if (showStationLabelAndImage) {
				const styles = marker.setStyle([
					new Style({
						image: new Icon({
							src: formatWeatherStateIcon({
								status: value,
								timeStep: new Date().toString(),
							}),
							opacity: 1,
							scale: 0.04,
						}),
					}),

					new Style({
						text: new Text({
							text: item.label,
							font: "12px",
							fill: new Fill({ color: "#000" }),
							stroke: new Stroke({ color: "#fff" }),
							offsetY: 28,
							textAlign: "center",
							textBaseline: "middle",
						}),
					}),
				]);

				marker.set("stationId", item.station_id);
				marker.set("priority", item.Priority);
				marker.set("defaultStyle", styles);
			} else {
				const isWind = selectedfilter === "wind_direction";

				if (isWind) {
					const windRadians = (value * Math.PI) / 180;

					const finalRotation = windRadians + (225 * Math.PI) / 180;

					marker.setStyle([
						new Style({
							image: new CircleStyle({
								radius: 14,
								fill: new Fill({ color: "#fff" }),
								stroke: new Stroke({ color: "#000000", width: 2 }),
							}),
						}),

						new Style({
							image: new Icon({
								src: windIcon,
								scale: 0.6,
								rotation: finalRotation,
								rotateWithView: true,

								anchor: [0.5, 0.5],
								anchorXUnits: "fraction",
								anchorYUnits: "fraction",
							}),
						}),
					]);
				} else {
					marker.setStyle(
						new Style({
							image: new CircleStyle({
								radius: 14,
								fill: new Fill({ color: "#fff" }),
								stroke: new Stroke({ color: "#000000", width: 2 }),
							}),
							text: new Text({
								text: String(value),
								textAlign: "center",
								textBaseline: "middle",
								fill: new Fill({ color: "#000000" }),
							}),
						}),
					);
				}
			}
		} else {
			marker.setStyle(
				new Style({
					image: new CircleStyle(),
				}),
			);
		}

		marker.setStyle(marker.getStyle());
		marker.set("stationId", item.station_id);
		marker.set("priority", item.Priority);
		marker.set("defaultStyle", marker.getStyle());

		return marker;
	});
};

export const featuresPolygons = (
	areaPolygon?: AreaPolygonType,
	selectedPolygonId?: string,
): Feature<Polygon>[] | undefined => {
	return areaPolygon?.map((poly) => {
		const feature = new Feature({
			geometry: new Polygon([poly.coords]).transform("EPSG:4326", "EPSG:3857"),
		});

		feature.setStyle(
			new Style({
				fill: new Fill({
					color:
						"color" in poly
							? hexToRgba(
									poly.color,
									selectedPolygonId
										? poly.id === selectedPolygonId
											? 1
											: 0.5
										: 1,
							  )
							: "rgba(37, 178, 74, 0.5)",
				}),
				stroke: new Stroke({
					color: "#00000",
					width: 1,
				}),
			}),
		);

		feature.setId(poly.id);
		feature.set("priority", 1);
		feature.set("defaultStyle", feature.getStyle());

		console.log(feature);

		return feature;
	});
};

export const addObservationMarker = ({
	mergedObsData,
	currentIndex,
	filter,
}: {
	mergedObsData: IMergedObservationAndStation[][];
	currentIndex: number;
	filter?: keyof observationsType[number]["data"];
}) => {
	const currentObsData = mergedObsData[currentIndex];

	if (!filter || !currentObsData) return [];

	return currentObsData
		.map((obsData) => {
			if (!obsData.coordinates) return;

			const styles: Style[] = [];

			const marker = new Feature({
				geometry: new Point(fromLonLat(obsData.coordinates)),
			});

			const findValue = obsData.data[filter] ?? "-";

			if (filter === "wind_direction") {
				styles.push(
					new Style({
						image: new CircleStyle({
							radius: 10,
							fill: new Fill({
								color: "#dbeafe",
							}),
							stroke: new Stroke({
								width: 3,
								color: "#133d61",
							}),
						}),
					}),

					typeof findValue === "number"
						? new Style({
								image: new Icon({
									src: windIcon,
									scale: 0.6,
									rotation: (findValue * Math.PI) / 180,
									rotateWithView: true,
									anchor: [0.5, 0.5],
									anchorXUnits: "fraction",
									anchorYUnits: "fraction",
									color: "#133d61",
								}),
						  })
						: new Style({
								text: new Text({
									text: String(findValue),
									font: "12px sans-serif",
									fill: new Fill({ color: "#133d61" }),
									textAlign: "center",
									textBaseline: "middle",
								}),
						  }),
				);
			} else {
				styles.push(
					new Style({
						image: new CircleStyle({
							radius: 10,
							fill: new Fill({
								color: "#dbeafe",
							}),
							stroke: new Stroke({
								width: 3,
								color: "#133d61",
							}),
						}),
						text: new Text({
							text: String(findValue),
							font: "bold 12px sans-serif",
							fill: new Fill({
								color: "#133d61",
							}),
							textAlign: "center",
							textBaseline: "middle",
						}),
					}),
				);
			}

			marker.setStyle(styles);
			marker.set("stationId", obsData.stationId);
			marker.set("priority", obsData.priority);
			marker.set("defaultStyle", styles);

			return marker;
		})
		.filter((item) => item !== undefined);
};

export const addGeoTiffLayer = ({
	geotiffFiles,
	imagerySpeed,
	currentIndex: initialIndex,
	setCurrentIndex,
	baseUrl,
}: {
	baseUrl: string;
	geotiffFiles: any[];
	imagerySpeed: number;
	currentIndex: number;
	setCurrentIndex: (value: number) => void;
}): GeoController & {
	layers: ImageLayer<ImageStatic>[];
} => {
	const MAX_CACHE_SIZE = 20;
	const FADE_DURATION = 500;

	let currentIndex = initialIndex;

	const extent = [3500000, 2000000, 6500000, 4500000];

	const sourceCache = new Map<number, ImageStatic>();

	const getSafeIndex = (index: number) =>
		Math.min(index, geotiffFiles.length - 1);

	const createSource = (index: number) => {
		const safeIndex = getSafeIndex(index);

		if (!sourceCache.has(safeIndex)) {
			const src = new ImageStatic({
				url: `${baseUrl}/${geotiffFiles[safeIndex].filename.replace(
					".tiff",
					".png",
				)}`,
				imageExtent: extent,
			});

			sourceCache.set(safeIndex, src);
		}

		return sourceCache.get(safeIndex)!;
	};

	const cleanupCache = () => {
		if (sourceCache.size <= MAX_CACHE_SIZE) return;

		for (const key of sourceCache.keys()) {
			if (key < currentIndex - 2) {
				sourceCache.delete(key);
			}
		}
	};

	const createLayer = (source: ImageStatic, zIndex: number) =>
		new ImageLayer({
			source,
			opacity: MAX_OPACITY_GEOTIFF,
			zIndex,
		});

	const layerA = createLayer(createSource(currentIndex), 15);

	let nextIndex = getSafeIndex(currentIndex + 1);

	const layerB = createLayer(createSource(nextIndex), 16);

	layerA.setOpacity(MAX_OPACITY_GEOTIFF);

	if (geotiffFiles.length < 2) {
		return {
			layers: [layerA],
			start: () => {},
			stop: () => {},
			setSpeed: () => {},
			seek: () => {},
		};
	}

	let activeLayer = layerA;
	let hiddenLayer = layerB;

	let isRunning = false;
	let animationFrameId: number | null = null;
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	let opacity = 0;
	let lastTime = 0;

	// Temps entre deux images.
	let currentSpeed = imagerySpeed;

	const animate = (time: number) => {
		if (!isRunning) return;

		if (!lastTime) {
			lastTime = time;
		}

		const delta = time - lastTime;
		const fadeDuration = Math.min(FADE_DURATION, currentSpeed);
		opacity += delta / fadeDuration;

		if (opacity >= 1) {
			opacity = 1;
			hiddenLayer.setOpacity(MAX_OPACITY_GEOTIFF);
			activeLayer.setOpacity(0);
			[activeLayer, hiddenLayer] = [hiddenLayer, activeLayer];
			currentIndex = (currentIndex + 1) % geotiffFiles.length;
			nextIndex = getSafeIndex(currentIndex + 1);
			hiddenLayer.setSource(createSource(nextIndex));
			cleanupCache();
			setCurrentIndex(currentIndex);

			opacity = 0;
			lastTime = 0;

			timeoutId = setTimeout(() => {
				if (isRunning) {
					animationFrameId = requestAnimationFrame(animate);
				}
			}, currentSpeed);

			return;
		}

		hiddenLayer.setOpacity(0);
		activeLayer.setOpacity(MAX_OPACITY_GEOTIFF);

		lastTime = time;

		animationFrameId = requestAnimationFrame(animate);
	};

	const start = () => {
		if (isRunning) return;

		isRunning = true;
		lastTime = 0;

		animationFrameId = requestAnimationFrame(animate);
	};

	const stop = () => {
		isRunning = false;

		if (animationFrameId !== null) {
			cancelAnimationFrame(animationFrameId);
			animationFrameId = null;
		}

		if (timeoutId !== null) {
			clearTimeout(timeoutId);
			timeoutId = null;
		}
	};

	const setSpeed = (newSpeed: number) => {
		currentSpeed = Math.max(0.1, newSpeed);
	};

	const seek = (index: number) => {
		currentIndex = getSafeIndex(index);
		nextIndex = getSafeIndex(currentIndex + 1);
		activeLayer.setSource(createSource(currentIndex));
		hiddenLayer.setSource(createSource(nextIndex));

		opacity = 0;
		lastTime = 0;

		setCurrentIndex(currentIndex);
	};

	return {
		layers: [layerA, layerB],
		start,
		stop,
		setSpeed,
		seek,
	};
};
